/**
 * 通用入场动画。
 *
 * 抽出来是因为三种场景本质是同一件事：元素出现时给它一个"从下往上到位"的过渡。
 *   · 路由切换（依赖 route key）
 *   · 弹层、提示条挂载（依赖空数组，只在挂载时播一次）
 *   · 列表整体刷新
 *
 * 只做入场不做出场：出场要么很快（显得没做），要么很慢（显得卡），
 * 收益远低于成本。状态切换的出场交给具体的退场动画去处理。
 */

import { useGSAP } from '@gsap/react';
import { useRef, type RefObject } from 'react';

import { MOTION, gsap, prefersReducedMotion } from './gsap';

export interface EnterOptions {
	/** 起始下移量 */
	y?: number;
	/** 起始缩放，1 表示不缩 */
	scale?: number;
	duration?: number;
	delay?: number;
	/** 变化时重新播放 */
	deps?: unknown[];
}

export function useEnterIn<T extends HTMLElement = HTMLDivElement>(
	options: EnterOptions = {},
): RefObject<T> {
	const ref = useRef<T | null>(null);
	const { y = 10, scale = 1, duration = MOTION.page, delay = 0, deps = [] } = options;

	useGSAP(
		() => {
			if (!ref.current || prefersReducedMotion()) return;

			gsap.fromTo(
				ref.current,
				{ autoAlpha: 0, y, scale },
				{
					autoAlpha: 1,
					y: 0,
					scale: 1,
					duration,
					delay,
					ease: MOTION.ease.out,
					overwrite: 'auto',
					// 收尾清掉内联 transform，避免影响后续的 FLIP 和定位
					clearProps: 'transform',
				},
			);
		},
		{ scope: ref, dependencies: deps, revertOnUpdate: true },
	);

	return ref as RefObject<T>;
}
